import { Fragment, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/data-table";
import { ChevronDown, ChevronRight, ChevronRightIcon } from "lucide-react";
import { cn } from "@/utils/classnames";
import { format } from "date-fns";
import useInvoiceSelection from "@/hooks/invoice/use-invoice-selection";
import { PAYABLE_STATUSES } from "./columns";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val ?? 0);

export const STATUS_GROUPS = [
  {
    key: "past_due",
    label: "Past Due",
    statuses: ["past_due", "unpaid_and_overdue"],
    colors: {
      header: "bg-red-50 border-red-100 text-red-700",
      dot: "bg-red-500",
      dotRing: "bg-red-100",
    },
  },
  {
    key: "unpaid",
    label: "Unpaid",
    statuses: ["unpaid"],
    colors: {
      header: "bg-amber-50 border-amber-100 text-amber-700",
      dot: "bg-amber-400",
      dotRing: "bg-amber-100",
    },
  },
  {
    key: "pending",
    label: "Pending",
    statuses: ["pending", "processing"],
    colors: {
      header: "bg-blue-50 border-blue-100 text-blue-700",
      dot: "bg-blue-500",
      dotRing: "bg-blue-100",
    },
  },
  {
    key: "scheduled",
    label: "Scheduled",
    statuses: ["draft", "scheduled"],
    colors: {
      header: "bg-gray-50 border-gray-200 text-gray-600",
      dot: "bg-gray-400",
      dotRing: "bg-gray-100",
    },
  },
  {
    key: "paid",
    label: "Paid",
    statuses: ["paid", "paid_off_platform"],
    colors: {
      header: "bg-green-50 border-green-100 text-green-700",
      dot: "bg-green-500",
      dotRing: "bg-green-100",
    },
  },
  {
    key: "failed",
    label: "Failed",
    statuses: ["failed"],
    colors: {
      header: "bg-red-50 border-red-100 text-red-700",
      dot: "bg-red-600",
      dotRing: "bg-red-100",
    },
  },
];

const QUICK_FILTER_MAP = {
  unpaid: ["unpaid"],
  past_due: ["past_due", "unpaid_and_overdue"],
  pending: ["pending", "processing"],
  scheduled: ["draft", "scheduled"],
  all: null,
};

export function DataTable({ columns, data, quickFilter = "all" }) {
  const [sorting, setSorting] = useState([{ id: "sent", desc: true }]);
  const [expanded, setExpanded] = useState(
    () => new Set(STATUS_GROUPS.map((g) => g.key))
  );

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  const sortedRows = table.getRowModel().rows;
  const activeFilterStatuses = QUICK_FILTER_MAP[quickFilter] ?? null;

  const visibleGroups = useMemo(
    () =>
      STATUS_GROUPS.filter((group) =>
        activeFilterStatuses
          ? group.statuses.some((s) => activeFilterStatuses.includes(s))
          : true
      ),
    [activeFilterStatuses]
  );

  const groupedRows = useMemo(() => {
    const map = {};
    for (const group of STATUS_GROUPS) {
      map[group.key] = sortedRows.filter((row) =>
        group.statuses.includes(
          row.original.invoice_info?.invoice_status?.toLowerCase() ?? ""
        )
      );
    }
    return map;
  }, [sortedRows]);

  const hasAnyRows = visibleGroups.some((g) => (groupedRows[g.key] ?? []).length > 0);

  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:block rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <Table>
          <TableHeader className="bg-skin-primary border-0">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-white">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {hasAnyRows ? (
              visibleGroups.map((group) => {
                const rows = groupedRows[group.key] ?? [];
                if (rows.length === 0) return null;
                const isOpen = activeFilterStatuses ? true : expanded.has(group.key);
                const groupTotal = rows.reduce(
                  (sum, r) => sum + (r.original.invoice_info?.invoice_total ?? 0),
                  0
                );

                return (
                  <Fragment key={group.key}>
                    <TableRow
                      className={cn(
                        "border-t select-none",
                        group.colors.header,
                        !activeFilterStatuses && "cursor-pointer"
                      )}
                      onClick={() => !activeFilterStatuses && toggleGroup(group.key)}
                    >
                      <TableCell colSpan={columns.length} className="py-3 px-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {!activeFilterStatuses &&
                              (isOpen ? (
                                <ChevronDown className="w-4 h-4 shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 shrink-0" />
                              ))}
                            <span className="font-semibold text-sm">{group.label}</span>
                            <span className="text-xs font-medium opacity-60">
                              {rows.length} {rows.length === 1 ? "invoice" : "invoices"}
                            </span>
                          </div>
                          <span className="text-sm font-semibold tabular-nums">
                            {formatCurrency(groupTotal)}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen &&
                      rows.map((row) => (
                        <TableRow
                          key={row.id}
                          className="bg-white hover:bg-gray-50 transition-colors"
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </Fragment>
                );
              })
            ) : (
              <TableRow className="bg-white">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-gray-400 text-sm"
                >
                  No invoices found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile */}
      <MobileGroupedList
        groups={visibleGroups}
        groupedRows={groupedRows}
        expanded={expanded}
        toggleGroup={toggleGroup}
        activeFilterStatuses={activeFilterStatuses}
      />
    </>
  );
}

function MobileGroupedList({ groups, groupedRows, expanded, toggleGroup, activeFilterStatuses }) {
  const select = useInvoiceSelection();
  const hasAny = groups.some((g) => (groupedRows[g.key] ?? []).length > 0);

  if (!hasAny) {
    return (
      <div className="lg:hidden bg-white rounded-xl border border-gray-200 py-12 text-center text-sm text-gray-400">
        No invoices found.
      </div>
    );
  }

  return (
    <div className="lg:hidden rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {groups.map((group) => {
        const rows = groupedRows[group.key] ?? [];
        if (rows.length === 0) return null;
        const isOpen = activeFilterStatuses ? true : expanded.has(group.key);
        const groupTotal = rows.reduce(
          (sum, r) => sum + (r.original.invoice_info?.invoice_total ?? 0),
          0
        );

        return (
          <div key={group.key} className="border-b border-gray-200 last:border-0">
            <button
              type="button"
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 text-sm font-semibold",
                group.colors.header,
                activeFilterStatuses ? "cursor-default" : "cursor-pointer"
              )}
              onClick={() => !activeFilterStatuses && toggleGroup(group.key)}
            >
              <div className="flex items-center gap-2">
                {!activeFilterStatuses &&
                  (isOpen ? (
                    <ChevronDown className="w-4 h-4 shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 shrink-0" />
                  ))}
                <span>{group.label}</span>
                <span className="text-xs font-medium opacity-60">
                  {rows.length} {rows.length === 1 ? "invoice" : "invoices"}
                </span>
              </div>
              <span className="font-semibold tabular-nums">{formatCurrency(groupTotal)}</span>
            </button>

            {isOpen && (
              <ul className="divide-y divide-gray-100 bg-white">
                {rows.map((row) => {
                  const inv = row.original;
                  const info = inv.invoice_info;
                  const status = info?.invoice_status?.toLowerCase() ?? "";
                  const isPayable = PAYABLE_STATUSES.has(status);
                  const dueDate = (() => {
                    if (!info?.invoice_due_date) return null;
                    const d = new Date(info.invoice_due_date);
                    return isNaN(d.getTime()) ? null : format(d, "MMM d, yyyy");
                  })();

                  return (
                    <li key={inv.invoice_id} className="px-4 py-4 flex items-center justify-between gap-4">
                      <div
                        className="flex items-start gap-3 min-w-0 flex-1 cursor-pointer"
                        onClick={() => select(inv)}
                      >
                        <span
                          className={cn(
                            "h-4 w-4 flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full",
                            group.colors.dotRing
                          )}
                        >
                          <span className={cn("h-2 w-2 rounded-full", group.colors.dot)} />
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                              {formatCurrency(info?.invoice_total)}
                            </span>
                            {info?.invoice_number && (
                              <span className="text-xs text-gray-400">
                                #{info.invoice_number}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap text-xs text-gray-500">
                            {info?.invoice_from?.name && (
                              <span>From {info.invoice_from.name}</span>
                            )}
                            {dueDate && (
                              <>
                                <span className="h-1 w-1 rounded-full bg-gray-300 inline-block" />
                                <span>Due {dueDate}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => select(inv)}
                        className={cn(
                          "flex-shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset flex items-center gap-1 transition-colors",
                          isPayable
                            ? "bg-skin-primary text-skin-a11y ring-transparent"
                            : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
                        )}
                      >
                        {isPayable ? "Pay" : "View"}
                        <ChevronRightIcon className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
